import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperadmin } from '../hooks/trSuperadmin';
import { useSuperadminPlatform } from '../hooks/trSuperadminPlatform';
import {
  School,
  Bus,
  Bell,
  AlertTriangle,
  TrendingUp,
  Wifi,
  WifiOff,
  X,
  MapPin,
  LogIn,
  LogOut,
  Info,
  Server,
  Users,
  Search,
  Plus,
  LayoutDashboard,
  UserCheck,
  ShieldAlert,
  ChevronRight,
  Database,
  Download,
  Trash2,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import InteractiveMap from '../components/InteractiveMap';

// ─── InfoTooltip ──────────────────────────────────────────────────────────────
// Supports hover (desktop) and click toggle (mobile)

function InfoTooltip({ text }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  const open = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 6, left: r.left + r.width / 2 });
    }
    setVisible(true);
  };

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={open}
        onMouseLeave={() => setVisible(false)}
        onClick={(e) => {
          e.stopPropagation();
          visible ? setVisible(false) : open();
        }}
        className="inline-flex items-center ml-1 cursor-help"
        aria-label="Plus d'informations"
      >
        <Info size={12} className="text-gray-400 hover:text-gray-600 transition-colors" />
      </span>
      {visible && (
        <div
          style={{ position: 'fixed', top: coords.top, left: Math.max(8, coords.left - 112), zIndex: 50 }}
          className="w-56 rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-xl leading-relaxed pointer-events-none"
        >
          {text}
        </div>
      )}
    </>
  );
}

// ─── KpiCard ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color = 'blue', loading, tooltip }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
  };
  return (
    <div className={`rounded-xl border p-4 flex items-start gap-3 ${colors[color]}`}>
      <div className="p-2 rounded-lg bg-white/60 flex-shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium opacity-70 flex items-center gap-0.5">
          <span className="truncate">{label}</span>
          {tooltip && <InfoTooltip text={tooltip} />}
        </p>
        {loading ? (
          <div className="h-6 w-14 bg-current opacity-10 rounded animate-pulse mt-1" />
        ) : (
          <p className="text-xl font-bold leading-tight">{value ?? '—'}</p>
        )}
        {sub && <p className="text-xs opacity-60 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

// ─── UsageBar ────────────────────────────────────────────────────────────────

function UsageBar({ count, max }) {
  if (!max) return <span className="text-xs text-gray-400">—</span>;
  const pct = Math.min(100, Math.round((count / max) * 100));
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-1.5 rounded-full bg-gray-200">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {count}/{max}
      </span>
    </div>
  );
}

// ─── GpsFreshness ────────────────────────────────────────────────────────────

function GpsFreshness({ timestamp }) {
  if (!timestamp)
    return (
      <span className="text-xs text-gray-400 flex items-center gap-1">
        <WifiOff size={12} /> jamais
      </span>
    );
  const ago = formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: fr });
  const isStale = Date.now() - new Date(timestamp).getTime() > 15 * 60 * 1000;
  return (
    <span className={`text-xs flex items-center gap-1 ${isStale ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
      {isStale ? <WifiOff size={12} /> : <Wifi size={12} />}
      {ago}
    </span>
  );
}

// ─── AlertItem ───────────────────────────────────────────────────────────────

function AlertItem({ alert }) {
  const styles = {
    critical: 'border-red-200 bg-red-50 text-red-700',
    warning: 'border-orange-200 bg-orange-50 text-orange-700',
    info: 'border-gray-200 bg-gray-50 text-gray-600',
  };
  const dots = { critical: 'bg-red-500', warning: 'bg-orange-400', info: 'bg-gray-400' };
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs ${styles[alert.severity]}`}>
      <div className="flex items-start gap-2">
        <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${dots[alert.severity]}`} />
        <span>{alert.message}</span>
      </div>
    </div>
  );
}

// ─── HealthBadge ─────────────────────────────────────────────────────────────

function HealthBadge({ score }) {
  if (score === undefined || score === null) return null;
  const [bg, text, dot] =
    score >= 80
      ? ['bg-green-100', 'text-green-700', 'bg-green-500']
      : score >= 50
        ? ['bg-yellow-100', 'text-yellow-700', 'bg-yellow-500']
        : ['bg-red-100', 'text-red-700', 'bg-red-500'];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${bg} ${text}`}
      title="Score de santé (GPS + admin + engagement + usage)"
    >
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
      {score}
    </span>
  );
}

// ─── TabBar ──────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Vue globale', icon: LayoutDashboard },
  { id: 'schools', label: 'Écoles', icon: School },
  { id: 'engagement', label: 'Engagement', icon: TrendingUp },
  { id: 'infra', label: 'Infrastructure', icon: Server },
];

function TabBar({ active, onChange }) {
  return (
    <nav
      className="flex overflow-x-auto border-b border-gray-200 mb-6 -mx-6 px-6 bg-white"
      aria-label="Navigation dashboard"
    >
      <div className="flex min-w-max">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              active === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ─── FIELD (form helper) ──────────────────────────────────────────────────────

const FIELD = ({ id, label, required, type = 'text', value, onChange, placeholder, className = '' }) => (
  <div>
    <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <input
      id={id}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-300 ${className}`}
    />
  </div>
);

// ─── CreateSchoolModal ────────────────────────────────────────────────────────

function CreateSchoolModal({ onClose, onCreate }) {
  const [form, setForm] = useState({
    name: '',
    code: '',
    subscription_plan: 'basic',
    address: '',
    city: '',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    max_students: '',
    max_buses: '',
    admin_email: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        code: form.code,
        subscription_plan: form.subscription_plan,
        admin_email: form.admin_email,
        ...(form.address && { address: form.address }),
        ...(form.city && { city: form.city }),
        ...(form.phone && { phone: form.phone }),
        ...(form.email && { email: form.email }),
        ...(form.latitude && { latitude: Number(form.latitude) }),
        ...(form.longitude && { longitude: Number(form.longitude) }),
        ...(form.max_students && { max_students: Number(form.max_students) }),
        ...(form.max_buses && { max_buses: Number(form.max_buses) }),
        ...(form.admin_first_name && { admin_first_name: form.admin_first_name }),
        ...(form.admin_last_name && { admin_last_name: form.admin_last_name }),
        ...(form.admin_password && { admin_password: form.admin_password }),
      };
      const result = await onCreate(payload);
      setCreated(result);
    } catch (err) {
      setError(err.message || 'Erreur création');
    } finally {
      setSubmitting(false);
    }
  };

  if (created)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="fixed inset-0 bg-black/30" onClick={onClose} />
        <div
          role="dialog"
          aria-modal="true"
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-green-700">École créée</h2>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400">
              <X size={16} />
            </button>
          </div>
          <p className="text-sm text-gray-700 mb-3">
            <strong>{created.school?.name}</strong> et son admin ont été créés.
          </p>
          {created.temp_password && (
            <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm">
              <p className="font-medium text-yellow-800 mb-1">Mot de passe temporaire</p>
              <p className="text-yellow-700">
                Email : <strong>{created.admin?.email}</strong>
              </p>
              <p className="text-yellow-700 font-mono text-base mt-1">{created.temp_password}</p>
              <p className="text-xs text-yellow-600 mt-2">
                Communiquer ces identifiants à l'administrateur de l'école.
              </p>
            </div>
          )}
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Fermer
          </button>
        </div>
      </div>
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/30" onClick={onClose} />
      <div role="dialog" aria-modal="true" className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <School size={16} className="text-gray-400" /> Nouvelle école
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400">
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[70vh] p-6 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">École</p>
            <div className="space-y-3">
              <FIELD
                id="school-name"
                label="Nom"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="École des Orangers"
              />
              <FIELD
                id="school-code"
                label="Code"
                required
                value={form.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="ECO01"
                className="uppercase"
              />
              <div>
                <label htmlFor="school-plan" className="block text-xs font-medium text-gray-700 mb-1">
                  Offre<span className="text-red-400 ml-0.5">*</span>
                </label>
                <select
                  id="school-plan"
                  value={form.subscription_plan}
                  onChange={(e) => set('subscription_plan', e.target.value)}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
                >
                  <option value="basic">Basic — IoT GPS (100 élèves · 5 bus)</option>
                  <option value="premium">Premium — GPS + app chauffeur (300 élèves · 20 bus)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FIELD
                  id="school-city"
                  label="Ville"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  placeholder="Casablanca"
                />
                <FIELD
                  id="school-phone"
                  label="Téléphone"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  placeholder="0522001122"
                />
              </div>
              <FIELD
                id="school-address"
                label="Adresse"
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
                placeholder="12 rue des Orangers"
              />
              <FIELD
                id="school-email"
                label="Email école"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="contact@ecole.ma"
              />
              <div className="grid grid-cols-2 gap-3">
                <FIELD
                  id="school-lat"
                  label="Latitude"
                  type="number"
                  value={form.latitude}
                  onChange={(e) => set('latitude', e.target.value)}
                  placeholder="33.5731"
                />
                <FIELD
                  id="school-lng"
                  label="Longitude"
                  type="number"
                  value={form.longitude}
                  onChange={(e) => set('longitude', e.target.value)}
                  placeholder="-7.5898"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FIELD
                  id="school-students"
                  label="Max élèves"
                  type="number"
                  value={form.max_students}
                  onChange={(e) => set('max_students', e.target.value)}
                  placeholder={form.subscription_plan === 'premium' ? '300' : '100'}
                />
                <FIELD
                  id="school-buses"
                  label="Max bus"
                  type="number"
                  value={form.max_buses}
                  onChange={(e) => set('max_buses', e.target.value)}
                  placeholder={form.subscription_plan === 'premium' ? '20' : '5'}
                />
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Compte administrateur</p>
            <div className="space-y-3">
              <FIELD
                id="admin-email"
                label="Email admin"
                required
                type="email"
                value={form.admin_email}
                onChange={(e) => set('admin_email', e.target.value)}
                placeholder="directeur@ecole.ma"
              />
              <div className="grid grid-cols-2 gap-3">
                <FIELD
                  id="admin-firstname"
                  label="Prénom"
                  value={form.admin_first_name}
                  onChange={(e) => set('admin_first_name', e.target.value)}
                  placeholder="Youssef"
                />
                <FIELD
                  id="admin-lastname"
                  label="Nom"
                  value={form.admin_last_name}
                  onChange={(e) => set('admin_last_name', e.target.value)}
                  placeholder="Directeur"
                />
              </div>
              <FIELD
                id="admin-password"
                label="Mot de passe"
                type="password"
                value={form.admin_password}
                onChange={(e) => set('admin_password', e.target.value)}
                placeholder="Laissez vide pour générer automatiquement"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {submitting ? 'Création…' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── GpsHealthSection ────────────────────────────────────────────────────────

function GpsHealthSection({ gpsHealth, loading }) {
  if (loading || !gpsHealth?.buses_offline?.length) return null;
  const buses = gpsHealth.buses_offline;
  return (
    <div className="mb-6 bg-white rounded-xl border border-red-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-red-100 bg-red-50 flex items-center gap-3">
        <WifiOff size={15} className="text-red-500" />
        <h2 className="font-semibold text-red-800 text-sm">
          {buses.length} bus hors ligne
          <span className="ml-2 font-normal text-red-400 text-xs">
            · {gpsHealth.summary.schools_affected} école{gpsHealth.summary.schools_affected > 1 ? 's' : ''} affectée
            {gpsHealth.summary.schools_affected > 1 ? 's' : ''}
          </span>
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        {buses.map((b) => {
          const isCritical = b.offline_since_min > 60;
          const duration = isCritical ? `${Math.round(b.offline_since_min / 60)}h` : `${b.offline_since_min} min`;
          return (
            <div key={b.bus_id} className="px-5 py-2.5 flex items-center gap-4">
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${isCritical ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}
              >
                {duration}
              </span>
              <span className="text-sm font-medium text-gray-900">Bus {b.bus_number ?? b.bus_id?.slice(0, 8)}</span>
              <span className="text-xs text-gray-500">{b.school_name ?? '—'}</span>
              <span className="ml-auto text-xs text-gray-400">
                {formatDistanceToNow(new Date(b.last_signal), { addSuffix: true, locale: fr })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SchoolCard (mobile) ──────────────────────────────────────────────────────

function SchoolCard({ school, onClick }) {
  const isPremium = school.subscription_plan === 'premium';
  return (
    <div className="p-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={onClick}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${school.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`}
          />
          <span className="font-medium text-gray-900 truncate">{school.name}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${isPremium ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
          >
            {isPremium ? 'Premium' : 'Basic'}
          </span>
          <HealthBadge score={school.healthScore} />
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2.5">
        <GpsFreshness timestamp={school.gps_last_signal} />
        <span>
          Admin:{' '}
          {school.admin_last_login
            ? formatDistanceToNow(new Date(school.admin_last_login), { addSuffix: true, locale: fr })
            : 'jamais'}
        </span>
        {school.usage.drivers.count > 0 && (
          <span>
            {school.usage.drivers.count} chauffeur{school.usage.drivers.count > 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Élèves</span>
            <span>
              {school.usage.students.count}/{school.usage.students.max}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-200">
            <div
              className={`h-1.5 rounded-full ${
                school.usage.students.max && school.usage.students.count / school.usage.students.max >= 0.9
                  ? 'bg-red-500'
                  : school.usage.students.max && school.usage.students.count / school.usage.students.max >= 0.7
                    ? 'bg-orange-400'
                    : 'bg-green-500'
              }`}
              style={{
                width: `${school.usage.students.max ? Math.min(100, Math.round((school.usage.students.count / school.usage.students.max) * 100)) : 0}%`,
              }}
            />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Bus</span>
            <span>
              {school.usage.buses.count}/{school.usage.buses.max}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-200">
            <div
              className={`h-1.5 rounded-full ${
                school.usage.buses.max && school.usage.buses.count / school.usage.buses.max >= 0.9
                  ? 'bg-red-500'
                  : school.usage.buses.max && school.usage.buses.count / school.usage.buses.max >= 0.7
                    ? 'bg-orange-400'
                    : 'bg-green-500'
              }`}
              style={{
                width: `${school.usage.buses.max ? Math.min(100, Math.round((school.usage.buses.count / school.usage.buses.max) * 100)) : 0}%`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end mt-2">
        <span className="text-xs text-blue-500 flex items-center gap-0.5">
          Détails <ChevronRight size={12} />
        </span>
      </div>
    </div>
  );
}

// ─── SchoolPanel (slide-over drill-down) ──────────────────────────────────────

function SchoolPanel({
  school,
  alerts,
  engagement,
  onClose,
  onImpersonate,
  onPatch,
  onDeleteGps,
  onExportGps,
  onGetDeletionPreview,
  onDeleteSchool,
  onDeleted,
}) {
  const [patching, setPatching] = useState(false);
  const [purgingGps, setPurgingGps] = useState(false);
  const [exportingGps, setExportingGps] = useState(false);
  const [gpsPurgeDate, setGpsPurgeDate] = useState(null);
  const [gpsExportRange, setGpsExportRange] = useState({ from: null, to: null });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmNameInput, setConfirmNameInput] = useState('');
  const [deleting, setDeleting] = useState(false);
  if (!school) return null;

  const schoolAlerts = alerts.filter((a) => a.school_id === school.id);
  const isPremium = school.subscription_plan === 'premium';
  const isActive = school.status !== 'inactive';
  const engData = engagement.find((e) => e.school_id === school.id);
  const activationPct = engData?.total > 0 ? Math.round((engData.activated / engData.total) * 100) : null;

  const handlePatch = async (patch) => {
    setPatching(true);
    try {
      await onPatch(school.id, patch);
    } finally {
      setPatching(false);
    }
  };

  const handleToggleStatus = () => {
    if (
      isActive &&
      !window.confirm(
        `Désactiver ${school.name} ? L'admin, les parents et les chauffeurs perdront l'accès immédiatement.`,
      )
    ) {
      return;
    }
    handlePatch({ status: isActive ? 'inactive' : 'active' });
  };

  const openDeleteDialog = async () => {
    setShowDeleteDialog(true);
    setConfirmNameInput('');
    setLoadingPreview(true);
    try {
      const preview = await onGetDeletionPreview(school.id);
      setDeletionPreview(preview);
    } catch {
      toast.error('Erreur lors du chargement du récapitulatif');
      setShowDeleteDialog(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeletionPreview(null);
    setConfirmNameInput('');
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await onDeleteSchool(school.id, confirmNameInput);
      toast.success(`${school.name} définitivement supprimée`);
      closeDeleteDialog();
      onDeleted?.();
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la suppression définitive');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 truncate">{school.name}</h3>
            <HealthBadge score={school.healthScore} />
          </div>
          <span className={`text-xs font-medium ${isPremium ? 'text-blue-600' : 'text-gray-500'}`}>
            {isPremium ? 'Premium' : 'Basic'}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onImpersonate(school.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
            title="Se connecter en tant qu'admin de cette école"
          >
            <LogIn size={13} />
            Se connecter
          </button>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400" aria-label="Fermer">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-2">
        <button
          disabled={patching}
          onClick={() => handlePatch({ subscription_plan: isPremium ? 'basic' : 'premium' })}
          className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Passer en {isPremium ? 'Basic' : 'Premium'}
        </button>
        <button
          disabled={patching}
          onClick={handleToggleStatus}
          className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
            isActive
              ? 'border-red-200 text-red-600 hover:bg-red-50'
              : 'border-green-200 text-green-600 hover:bg-green-50'
          }`}
        >
          {isActive ? 'Désactiver' : 'Réactiver'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Usage */}
        <section>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            Utilisation
            <InfoTooltip text="Usage des ressources vs limites du plan. Rouge = ≥90% — risque de blocage." />
          </h4>
          <div className="space-y-2">
            {[
              { label: 'Élèves', count: school.usage.students.count, max: school.usage.students.max },
              { label: 'Bus', count: school.usage.buses.count, max: school.usage.buses.max },
            ].map(({ label, count, max }) => {
              const pct = max ? Math.round((count / max) * 100) : 0;
              const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-blue-500';
              return (
                <div key={label}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{label}</span>
                    <span>
                      {count}/{max}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100">
                    <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            <p className="text-xs text-gray-500">{school.usage.drivers.count} chauffeur(s)</p>
          </div>
        </section>

        {/* GPS */}
        <section>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            Signal GPS
            <InfoTooltip text="Dernière position reçue d'un bus de cette école. Rouge si >15 min." />
          </h4>
          <GpsFreshness timestamp={school.gps_last_signal} />
        </section>

        {/* Engagement parents */}
        {engData && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
              Engagement parents
              <InfoTooltip text="Comptes activés = parents ayant défini leur mot de passe. Très actifs = ≥2 ouvertures/jour." />
            </h4>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Activation</span>
                <span
                  className={`font-medium ${activationPct >= 70 ? 'text-green-600' : activationPct >= 40 ? 'text-orange-500' : 'text-gray-400'}`}
                >
                  {engData.activated}/{engData.total} ({activationPct}%)
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>Très actifs</span>
                <span>{engData.highly_engaged ?? 0}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-600">
                <span>Jamais connectés</span>
                <span className={engData.never_connected > 0 ? 'text-orange-500' : 'text-gray-500'}>
                  {engData.never_connected ?? 0}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Admin last login */}
        <section>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Dernière connexion admin</h4>
          <p className="text-xs text-gray-600">
            {school.admin_last_login ? (
              formatDistanceToNow(new Date(school.admin_last_login), { addSuffix: true, locale: fr })
            ) : (
              <span className="text-gray-400">Jamais connecté</span>
            )}
          </p>
        </section>

        {/* Alertes pour cette école */}
        {schoolAlerts.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Alertes ({schoolAlerts.length})
            </h4>
            <div className="space-y-1.5">
              {schoolAlerts.map((a, i) => (
                <AlertItem key={i} alert={a} />
              ))}
            </div>
          </section>
        )}

        {/* Données GPS */}
        <section className="border-t pt-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
            <Database size={12} />
            Données GPS
          </h4>

          {/* Export — plage de dates */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1.5">Exporter positions CSV</p>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors text-left">
                  <CalendarIcon size={12} />
                  {gpsExportRange.from && gpsExportRange.to
                    ? `${format(gpsExportRange.from, 'dd/MM/yy')} – ${format(gpsExportRange.to, 'dd/MM/yy')}`
                    : 'Sélectionner une période'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={gpsExportRange}
                  onSelect={setGpsExportRange}
                  numberOfMonths={1}
                  locale={fr}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
            <button
              disabled={exportingGps || !gpsExportRange.from}
              onClick={async () => {
                setExportingGps(true);
                try {
                  const r = await onExportGps({
                    schoolId: school.id,
                    fromDate: gpsExportRange.from ? format(gpsExportRange.from, 'yyyy-MM-dd') : undefined,
                    toDate: gpsExportRange.to ? format(gpsExportRange.to, 'yyyy-MM-dd') : undefined,
                    schoolName: school.name,
                  });
                  if (r?.truncated) toast.warning('Export limité à 10 000 lignes');
                  else toast.success('Export téléchargé');
                } catch {
                  toast.error('Export échoué');
                } finally {
                  setExportingGps(false);
                }
              }}
              className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download size={12} />
              {exportingGps ? 'Export…' : 'Télécharger CSV'}
            </button>
          </div>

          {/* Purge — date unique */}
          <div>
            <p className="text-xs text-gray-500 mb-1.5">Supprimer positions avant</p>
            <Popover>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors text-left">
                  <CalendarIcon size={12} />
                  {gpsPurgeDate ? format(gpsPurgeDate, 'dd/MM/yyyy') : 'Choisir une date'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  initialFocus
                  mode="single"
                  selected={gpsPurgeDate}
                  onSelect={setGpsPurgeDate}
                  locale={fr}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
            <button
              disabled={purgingGps || !gpsPurgeDate}
              onClick={async () => {
                if (
                  !window.confirm(
                    `Supprimer toutes les positions de ${school.name} avant le ${format(gpsPurgeDate, 'dd/MM/yyyy')} ? Cette action est irréversible.`,
                  )
                )
                  return;
                setPurgingGps(true);
                try {
                  const r = await onDeleteGps({ beforeDate: format(gpsPurgeDate, 'yyyy-MM-dd'), schoolId: school.id });
                  toast.success(`${r?.deleted_count ?? 0} positions supprimées`);
                  setGpsPurgeDate(null);
                } catch {
                  toast.error('Suppression échouée');
                } finally {
                  setPurgingGps(false);
                }
              }}
              className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <Trash2 size={12} />
              {purgingGps ? 'Suppression…' : 'Supprimer'}
            </button>
          </div>
        </section>

        {/* Zone dangereuse — hard-delete, uniquement si déjà désactivée */}
        {!isActive && (
          <section className="border-t pt-4">
            <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <AlertTriangle size={12} />
              Zone dangereuse
            </h4>
            <p className="text-xs text-gray-500 mb-2">
              Supprime définitivement l'école et toutes ses données (élèves, parents, chauffeurs, trajets, photos…).
              Action irréversible.
            </p>
            <button
              onClick={openDeleteDialog}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors"
            >
              <Trash2 size={12} />
              Supprimer définitivement
            </button>
          </section>
        )}
      </div>

      {/* Dialog confirmation suppression définitive */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => (open ? null : closeDeleteDialog())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer définitivement {school.name}</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Toutes les données ci-dessous seront supprimées.
            </DialogDescription>
          </DialogHeader>
          {loadingPreview ? (
            <div className="py-8 text-center text-sm text-gray-500">Chargement du récapitulatif…</div>
          ) : (
            deletionPreview && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(deletionPreview.counts).map(([table, count]) => (
                    <div key={table} className="flex justify-between px-2 py-1 bg-gray-50 rounded">
                      <span className="text-gray-500">{table}</span>
                      <span className="font-medium">{count}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Tapez <span className="font-mono font-semibold">{school.name}</span> pour confirmer
                  </label>
                  <input
                    type="text"
                    value={confirmNameInput}
                    onChange={(e) => setConfirmNameInput(e.target.value)}
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    autoComplete="off"
                  />
                </div>
              </div>
            )
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDeleteDialog}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={deleting || confirmNameInput !== school.name}
              onClick={confirmDelete}
            >
              {deleting ? 'Suppression…' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Engagement stacked bars ──────────────────────────────────────────────────

const ENGAGEMENT_SEGMENTS = [
  { key: 'highly_engaged', bar: 'bg-blue-500', name: 'Très actifs' },
  { key: 'partially_engaged', bar: 'bg-teal-400', name: 'Partiels' },
  { key: 'passive', bar: 'bg-orange-300', name: 'Passifs' },
  { key: 'never_connected', bar: 'bg-gray-200', name: 'Jamais connectés' },
];

function EngagementBars({ engagement, loading }) {
  if (loading) return <div className="h-32 bg-gray-50 rounded-lg animate-pulse" />;
  if (!engagement?.length) return <p className="text-sm text-gray-400 text-center py-8">Aucune donnée d'engagement</p>;

  const totals = engagement.reduce(
    (acc, s) => ({
      total: acc.total + (s.total || 0),
      activated: acc.activated + (s.activated || 0),
      highly_engaged: acc.highly_engaged + (s.highly_engaged || 0),
      partially_engaged: acc.partially_engaged + (s.partially_engaged || 0),
      passive: acc.passive + (s.passive || 0),
      never_connected: acc.never_connected + (s.never_connected || 0),
    }),
    { total: 0, activated: 0, highly_engaged: 0, partially_engaged: 0, passive: 0, never_connected: 0 },
  );

  const totalPct = totals.total > 0 ? Math.round((totals.activated / totals.total) * 100) : 0;

  const renderBar = (data, key) => {
    const seg = ENGAGEMENT_SEGMENTS.find((s) => s.key === key);
    const pct = data.total > 0 ? Math.round((data[key] / data.total) * 100) : 0;
    if (!pct) return null;
    return (
      <div
        key={key}
        className={`h-full ${seg.bar} transition-all`}
        style={{ width: `${pct}%` }}
        title={`${seg.name} : ${data[key]} (${pct}%)`}
      />
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {ENGAGEMENT_SEGMENTS.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${s.bar}`} />
            <span className="text-xs text-gray-500">{s.name}</span>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {engagement.map((school) => {
          const activationPct = school.total > 0 ? Math.round((school.activated / school.total) * 100) : 0;
          return (
            <div key={school.school_id} className="flex items-center gap-3">
              <span className="text-xs text-gray-700 w-36 truncate font-medium flex-shrink-0">
                {school.school_name}
              </span>
              <div className="flex-1 h-4 rounded-full overflow-hidden bg-gray-100 flex">
                {ENGAGEMENT_SEGMENTS.map(({ key }) => renderBar(school, key))}
              </div>
              <span
                className={`text-xs font-medium w-32 text-right flex-shrink-0 tabular-nums ${
                  activationPct >= 70 ? 'text-green-600' : activationPct >= 40 ? 'text-orange-500' : 'text-gray-400'
                }`}
              >
                {school.activated}/{school.total} · {activationPct}%
              </span>
            </div>
          );
        })}
      </div>

      {engagement.length > 1 && (
        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-100">
          <span className="text-xs font-semibold text-gray-500 w-36 flex-shrink-0">Total plateforme</span>
          <div className="flex-1 h-4 rounded-full overflow-hidden bg-gray-100 flex">
            {ENGAGEMENT_SEGMENTS.map(({ key }) => renderBar(totals, key))}
          </div>
          <span
            className={`text-xs font-semibold w-32 text-right flex-shrink-0 tabular-nums ${
              totalPct >= 70 ? 'text-green-600' : totalPct >= 40 ? 'text-orange-500' : 'text-gray-400'
            }`}
          >
            {totals.activated}/{totals.total} · {totalPct}%
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Trend charts ─────────────────────────────────────────────────────────────

function TrendCharts({ trends, trendsLoading, trendDays, setTrendDays, overview }) {
  const DAYS_OPTIONS = [7, 30, 90];
  const PIE_COLORS = ['#6366f1', '#94a3b8'];

  const pieData = overview
    ? [
        { name: 'Basic', value: overview.schools.basic },
        { name: 'Premium', value: overview.schools.premium },
      ]
    : [];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Trajets */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <TrendingUp size={15} className="text-gray-400" />
              Trajets complétés / jour
              <InfoTooltip text="Trajets avec statut 'completed' par jour calendaire, toutes écoles confondues." />
            </h3>
            <div className="flex gap-1">
              {DAYS_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setTrendDays(d)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${trendDays === d ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  {d}j
                </button>
              ))}
            </div>
          </div>
          {trendsLoading ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Chargement…</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={trends?.trips_by_day ?? []}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={24} />
                <Tooltip labelFormatter={(d) => d} formatter={(v) => [v, 'Trajets']} />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie tiers */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
            <School size={15} className="text-gray-400" />
            Répartition tiers
            <InfoTooltip text="Basic : IoT GPS seul. Premium : GPS + app chauffeur + pointage élèves." />
          </h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={60}
                label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}
                labelLine={false}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Notifications */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
            <Bell size={15} className="text-gray-400" />
            Notifications envoyées / jour
            <InfoTooltip text="Volume total de notifications push toutes écoles confondues." />
          </h3>
          {trendsLoading ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Chargement…</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={trends?.notifications_by_day ?? []}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={24} />
                <Tooltip labelFormatter={(d) => d} formatter={(v) => [v, 'Notifs']} />
                <Bar dataKey="count" fill="#f59e0b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Opens app parent */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
            <Wifi size={15} className="text-gray-400" />
            Ouvertures app parent / jour
            <InfoTooltip text="Ouvertures de l'app mobile parent par jour (throttle 30 min/session)." />
          </h3>
          {trendsLoading ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Chargement…</div>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={trends?.parent_opens_by_day ?? []}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={24} />
                <Tooltip labelFormatter={(d) => d} formatter={(v) => [v, 'Opens']} />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── InfraTab ─────────────────────────────────────────────────────────────────

function InfraTab({ onDeleteGps }) {
  const navigate = useNavigate();
  const { platformUsage, platformLoading } = useSuperadminPlatform();
  const [globalPurgeDate, setGlobalPurgeDate] = useState(null);
  const [purgingGlobal, setPurgingGlobal] = useState(false);

  const sb = platformUsage?.supabase;
  const fb = platformUsage?.firebase;
  const q = platformUsage?.notification_queue;

  const dbPct = sb?.db_usage_pct ?? 0;

  const metrics = [
    {
      label: 'Base de données',
      value: sb?.available ? `${dbPct}%` : '—',
      sub: sb?.db_size_pretty ? `${sb.db_size_pretty} utilisés` : 'Données indisponibles',
      icon: Server,
      color: dbPct >= 90 ? 'red' : dbPct >= 70 ? 'orange' : 'blue',
      tooltip: 'Espace disque PostgreSQL utilisé. Rouge = ≥90% — passage en plan supérieur recommandé.',
    },
    {
      label: 'Tokens FCM actifs',
      value: fb?.active_tokens ?? '—',
      sub: fb?.stale_tokens > 0 ? `${fb.stale_tokens} expirés à purger` : 'Aucun expiré',
      icon: Bell,
      color: 'blue',
      tooltip: 'Appareils avec un token FCM valide (is_active=true). Représente la couverture push.',
    },
    {
      label: 'Livraison notifications 7j',
      value: fb?.delivery_rate_7d !== null && fb?.delivery_rate_7d !== undefined ? `${fb.delivery_rate_7d}%` : '—',
      sub: fb?.failed_7d > 0 ? `${fb.failed_7d} échecs` : 'Aucun échec',
      icon: TrendingUp,
      color: fb?.delivery_rate_7d < 80 ? 'red' : fb?.delivery_rate_7d < 95 ? 'orange' : 'green',
      tooltip: '(sent + delivered + read) / total sur 7 jours. <80% = problème FCM critique.',
    },
    {
      label: 'File de notifications',
      value: q ? `${q.pending ?? 0} en attente` : '—',
      sub: q?.failed > 0 ? `${q.failed} échoués` : 'Aucun échec',
      icon: Bell,
      color: (q?.failed ?? 0) > 0 ? 'red' : (q?.pending ?? 0) > 100 ? 'orange' : 'green',
      tooltip: 'Jobs pending en attente du NotificationWorker. Failed élevé = vérifier credentials FCM.',
    },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {metrics.map((m) => (
          <KpiCard
            key={m.label}
            label={m.label}
            value={m.value}
            sub={m.sub}
            icon={m.icon}
            color={m.color}
            loading={platformLoading}
            tooltip={m.tooltip}
          />
        ))}
      </div>

      {/* Supabase DB detail */}
      {!platformLoading && sb?.available && sb.top_tables?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Server size={15} className="text-gray-400" />
            Tables les plus lourdes
            <InfoTooltip text="Taille totale (données + index). Utile pour identifier les tables candidates à l'archivage." />
          </h3>
          <div className="space-y-2">
            {sb.top_tables.map((t) => {
              const tpct = sb.db_size_bytes > 0 ? Math.round((t.size_bytes / sb.db_size_bytes) * 100) : 0;
              return (
                <div key={t.name} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-40 truncate">{t.name}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-100">
                    <div className="h-1.5 rounded-full bg-indigo-300" style={{ width: `${tpct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-14 text-right">{t.size}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Nettoyage GPS — suppression globale écoles Basic */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Database size={15} className="text-gray-400" />
          Nettoyage GPS — Écoles Basic
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Supprime toutes les positions des écoles Basic antérieures à la date choisie. Les écoles Premium ne sont pas
          affectées. Cette action est irréversible.
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                <CalendarIcon size={14} />
                {globalPurgeDate ? format(globalPurgeDate, 'dd/MM/yyyy') : 'Choisir une date limite'}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="single"
                selected={globalPurgeDate}
                onSelect={setGlobalPurgeDate}
                locale={fr}
                disabled={{ after: new Date() }}
              />
            </PopoverContent>
          </Popover>
          <button
            disabled={purgingGlobal || !globalPurgeDate}
            onClick={async () => {
              if (
                !window.confirm(
                  `Supprimer toutes les positions Basic avant le ${format(globalPurgeDate, 'dd/MM/yyyy')} ? Cette action est irréversible.`,
                )
              )
                return;
              setPurgingGlobal(true);
              try {
                const r = await onDeleteGps({ beforeDate: format(globalPurgeDate, 'yyyy-MM-dd') });
                toast.success(`${r?.deleted_count ?? 0} positions supprimées`);
                setGlobalPurgeDate(null);
              } catch {
                toast.error('Suppression échouée');
              } finally {
                setPurgingGlobal(false);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Trash2 size={14} />
            {purgingGlobal ? 'Suppression…' : 'Supprimer'}
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={() => navigate('/superadmin/platform')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 text-sm font-medium transition-colors"
        >
          <Server size={15} />
          Voir l'infrastructure complète
        </button>
      </div>
    </div>
  );
}

// ─── OverviewTab ─────────────────────────────────────────────────────────────

function OverviewTab({
  overview,
  overviewLoading,
  alerts,
  alertsLoading,
  gpsHealth,
  gpsHealthLoading,
  gpsPositions,
  gpsPositionsLoading,
  globalActivationRate,
  engagementLoading,
  atRiskCount,
  schoolsLoading,
}) {
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;

  const vehicles = gpsPositions.map((p) => ({
    id: p.id,
    bus_id: p.bus_id,
    bus_number: p.bus_number ?? p.id?.slice(0, 8),
    latitude: p.latitude,
    longitude: p.longitude,
    status: p.status,
    heading: p.heading,
    speed: p.speed,
    buffered: false,
    driver_name: p.school_name,
  }));

  return (
    <div>
      {/* 8 KPI Cards — 2 cols mobile, 4 cols desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Écoles actives"
          icon={School}
          color="blue"
          loading={overviewLoading}
          value={overview ? `${overview.schools.active}/${overview.schools.total}` : null}
          tooltip="Écoles avec statut actif sur le total. Inactive = accès suspendu."
        />
        <KpiCard
          label="Basic / Premium"
          icon={TrendingUp}
          color="purple"
          loading={overviewLoading}
          value={overview ? `${overview.schools.basic} / ${overview.schools.premium}` : null}
          tooltip="Basic : traceur GPS IoT. Premium : GPS + app chauffeur + pointage élèves."
        />
        <KpiCard
          label="Trajets actifs"
          icon={Bus}
          color="green"
          loading={overviewLoading}
          value={overview?.fleet.active_trips}
          tooltip="Trajets en cours à cet instant sur toutes les écoles (statut = active)."
        />
        <KpiCard
          label="Bus GPS en ligne"
          icon={Wifi}
          color="green"
          loading={overviewLoading}
          value={overview?.fleet.buses_online}
          sub="< 15 min"
          tooltip="Bus ayant envoyé une position GPS il y a moins de 15 minutes."
        />
        <KpiCard
          label="Notifications 24h"
          icon={Bell}
          color="blue"
          loading={overviewLoading}
          value={overview?.notifications_24h}
          tooltip="Notifications push envoyées aux parents dans les dernières 24 heures."
        />
        <KpiCard
          label="Alertes actives"
          icon={AlertTriangle}
          loading={alertsLoading}
          value={alertsLoading ? null : `${criticalCount} crit. · ${warningCount} warn.`}
          color={criticalCount > 0 ? 'red' : warningCount > 0 ? 'orange' : 'green'}
          tooltip="Bus offline >15min, écoles proches de leur limite, comptes inactifs. Triées par criticité."
        />
        <KpiCard
          label="Activation parents"
          icon={UserCheck}
          loading={engagementLoading}
          value={globalActivationRate !== null ? `${globalActivationRate}%` : '—'}
          sub="comptes activés"
          color={
            globalActivationRate === null
              ? 'blue'
              : globalActivationRate >= 70
                ? 'green'
                : globalActivationRate >= 40
                  ? 'orange'
                  : 'red'
          }
          tooltip="Pourcentage de parents ayant défini leur mot de passe. Faible = onboarding insuffisant."
        />
        <KpiCard
          label="Écoles à risque"
          icon={ShieldAlert}
          loading={schoolsLoading}
          value={atRiskCount}
          sub="score santé < 50"
          color={atRiskCount === 0 ? 'green' : atRiskCount <= 2 ? 'orange' : 'red'}
          tooltip="Écoles actives avec score santé <50 (GPS hors ligne, admin inactif ou engagement faible)."
        />
      </div>

      {/* GPS offline alert */}
      <GpsHealthSection gpsHealth={gpsHealth} loading={gpsHealthLoading} />

      {/* Alerts + GPS Map */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Alertes */}
        <div className="w-full lg:w-80 flex-shrink-0 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 flex items-center gap-1 text-sm">
              <AlertTriangle size={15} className="text-gray-400 mr-1" />
              Alertes
              <InfoTooltip text="🔴 Critical : bus offline >1h ou école à 100%. 🟠 Warning : bus offline <1h ou école ≥90%. 🔵 Info : comptes inactifs." />
            </h2>
            {!alertsLoading && <span className="text-xs text-gray-400">{alerts.length}</span>}
          </div>
          <div className="p-3 space-y-2 max-h-80 lg:max-h-[400px] overflow-y-auto">
            {alertsLoading && <div className="py-6 text-center text-sm text-gray-400">Chargement…</div>}
            {!alertsLoading && alerts.length === 0 && (
              <div className="py-6 text-center text-sm text-green-600 font-medium">Aucune alerte</div>
            )}
            {alerts.map((alert, i) => (
              <AlertItem key={i} alert={alert} />
            ))}
          </div>
        </div>

        {/* GPS Map */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 overflow-hidden min-h-[280px]">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
            <MapPin size={15} className="text-gray-400" />
            <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-1">
              Positions GPS en temps réel — toutes écoles
              <InfoTooltip text="Dernière position connue de chaque bus, toutes écoles confondues. Rafraîchissement toutes les 30 s." />
            </h2>
            {!gpsPositionsLoading && <span className="text-xs text-gray-400 ml-auto">{vehicles.length} bus</span>}
          </div>
          {gpsPositionsLoading ? (
            <div className="h-[280px] sm:h-[360px] flex items-center justify-center text-gray-400 text-sm">
              Chargement…
            </div>
          ) : vehicles.length === 0 ? (
            <div className="h-[280px] sm:h-[360px] flex items-center justify-center text-gray-400 text-sm">
              Aucun bus actif
            </div>
          ) : (
            <InteractiveMap vehicles={vehicles} height="360px" />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SchoolsTab ───────────────────────────────────────────────────────────────

function SchoolsTab({ schools, schoolsLoading, schoolFilter, setSchoolFilter, onSelectSchool }) {
  const filteredSchools = schools.filter((s) => {
    if (schoolFilter.query && !s.name.toLowerCase().includes(schoolFilter.query.toLowerCase())) return false;
    if (schoolFilter.tier !== 'all' && s.subscription_plan !== schoolFilter.tier) return false;
    if (schoolFilter.status !== 'all' && s.status !== schoolFilter.status) return false;
    return true;
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Filter bar */}
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2 mr-2 text-sm">
          <School size={15} className="text-gray-400" />
          Écoles
          <span className="text-xs font-normal text-gray-400">
            {filteredSchools.length !== schools.length ? `${filteredSchools.length}/${schools.length}` : schools.length}
          </span>
        </h2>
        <div className="relative flex-1 min-w-[140px] max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher…"
            value={schoolFilter.query}
            onChange={(e) => setSchoolFilter((f) => ({ ...f, query: e.target.value }))}
            className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-300"
          />
        </div>
        <select
          value={schoolFilter.tier}
          onChange={(e) => setSchoolFilter((f) => ({ ...f, tier: e.target.value }))}
          className="text-xs rounded-lg border border-gray-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
        >
          <option value="all">Tous les tiers</option>
          <option value="basic">Basic</option>
          <option value="premium">Premium</option>
        </select>
        <select
          value={schoolFilter.status}
          onChange={(e) => setSchoolFilter((f) => ({ ...f, status: e.target.value }))}
          className="text-xs rounded-lg border border-gray-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
        >
          <option value="all">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {/* Vue cartes — mobile uniquement */}
      <div className="md:hidden divide-y divide-gray-100">
        {schoolsLoading && <div className="p-8 text-center text-gray-400 text-sm">Chargement…</div>}
        {!schoolsLoading && filteredSchools.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-sm">
            {schools.length === 0 ? 'Aucune école' : 'Aucun résultat'}
          </div>
        )}
        {filteredSchools.map((school) => (
          <SchoolCard key={school.id} school={school} onClick={() => onSelectSchool(school)} />
        ))}
      </div>

      {/* Tableau — desktop uniquement */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-[1000px] w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">École</th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Tier <InfoTooltip text="Basic (IoT GPS) ou Premium (GPS + app chauffeur)." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Statut <InfoTooltip text="Active : l'école peut se connecter. Inactive : accès suspendu." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Santé{' '}
                  <InfoTooltip text="Score 0-100 : GPS fraîcheur (40pts) + login admin (30pts) + activation parents (20pts) + headroom plan (10pts)." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Élèves <InfoTooltip text="Élèves inscrits / limite max. Rouge = ≥90%." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Bus <InfoTooltip text="Bus enregistrés / limite max." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Chauffeurs <InfoTooltip text="Chauffeurs actifs. Disponible en Premium." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Dernier GPS <InfoTooltip text="Dernière position GPS. Rouge si >15 min." />
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="flex items-center gap-0.5">
                  Dernier login <InfoTooltip text="Dernière connexion admin au dashboard web." />
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {schoolsLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">
                  Chargement…
                </td>
              </tr>
            )}
            {!schoolsLoading && filteredSchools.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-400 text-sm">
                  {schools.length === 0 ? 'Aucune école' : 'Aucun résultat'}
                </td>
              </tr>
            )}
            {filteredSchools.map((school) => (
              <tr
                key={school.id}
                className="hover:bg-blue-50 transition-colors cursor-pointer"
                onClick={() => onSelectSchool(school)}
              >
                <td className="px-4 py-3 font-medium text-gray-900">{school.name}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      school.subscription_plan === 'premium' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {school.subscription_plan === 'premium' ? 'Premium' : 'Basic'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                      school.status === 'active' ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${school.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`}
                    />
                    {school.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <HealthBadge score={school.healthScore} />
                </td>
                <td className="px-4 py-3">
                  <UsageBar count={school.usage.students.count} max={school.usage.students.max} />
                </td>
                <td className="px-4 py-3">
                  <UsageBar count={school.usage.buses.count} max={school.usage.buses.max} />
                </td>
                <td className="px-4 py-3 text-gray-600">{school.usage.drivers.count}</td>
                <td className="px-4 py-3">
                  <GpsFreshness timestamp={school.gps_last_signal} />
                </td>
                <td className="px-4 py-3">
                  {school.admin_last_login ? (
                    <span className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(school.admin_last_login), { addSuffix: true, locale: fr })}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">jamais</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── EngagementTab ────────────────────────────────────────────────────────────

function EngagementTab({ trends, trendsLoading, trendDays, setTrendDays, overview, engagement, engagementLoading }) {
  const totals = engagement.reduce(
    (acc, s) => ({
      total: acc.total + (s.total || 0),
      activated: acc.activated + (s.activated || 0),
      highly_engaged: acc.highly_engaged + (s.highly_engaged || 0),
      never_connected: acc.never_connected + (s.never_connected || 0),
    }),
    { total: 0, activated: 0, highly_engaged: 0, never_connected: 0 },
  );

  const globalPct = totals.total > 0 ? Math.round((totals.activated / totals.total) * 100) : null;

  return (
    <div className="space-y-6">
      {/* KPI résumé engagement */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Parents inscrits"
          loading={engagementLoading}
          value={engagementLoading ? null : totals.total}
          icon={Users}
          color="blue"
          tooltip="Total des comptes parents créés sur toutes les écoles."
        />
        <KpiCard
          label="Comptes activés"
          loading={engagementLoading}
          value={engagementLoading ? null : `${totals.activated} (${globalPct ?? 0}%)`}
          icon={UserCheck}
          color={globalPct === null ? 'blue' : globalPct >= 70 ? 'green' : globalPct >= 40 ? 'orange' : 'red'}
          tooltip="Parents ayant défini leur mot de passe. % = taux d'activation global toutes écoles."
        />
        <KpiCard
          label="Très actifs (30j)"
          loading={engagementLoading}
          value={engagementLoading ? null : totals.highly_engaged}
          icon={TrendingUp}
          color="green"
          tooltip="Parents avec ≥2 ouvertures/jour en moyenne sur les 30 derniers jours."
        />
        <KpiCard
          label="Jamais connectés"
          loading={engagementLoading}
          value={engagementLoading ? null : totals.never_connected}
          icon={WifiOff}
          color={totals.never_connected === 0 ? 'green' : totals.never_connected > 10 ? 'red' : 'orange'}
          tooltip="Parents sans aucune ouverture de l'app sur 30 jours. Cible prioritaire pour le support."
        />
      </div>

      <TrendCharts
        trends={trends}
        trendsLoading={trendsLoading}
        trendDays={trendDays}
        setTrendDays={setTrendDays}
        overview={overview}
      />

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-2 mb-4">
          <Users size={16} className="text-gray-400" />
          Engagement parents — 30 derniers jours
          <InfoTooltip text="Classification des parents selon leur activité. Très actifs : ≥2 ouvertures/jour actif. Partiels : ≥1. Passifs : <1. Jamais : aucune activité sur 30j." />
        </h2>
        <EngagementBars engagement={engagement} loading={engagementLoading} />
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function SuperadminDashboard() {
  const {
    overview,
    overviewLoading,
    schools,
    schoolsLoading,
    alerts,
    alertsLoading,
    gpsHealth,
    gpsHealthLoading,
    gpsPositions,
    gpsPositionsLoading,
    engagement,
    engagementLoading,
    trends,
    trendsLoading,
    trendDays,
    setTrendDays,
    selectedSchool,
    setSelectedSchool,
    schoolFilter,
    setSchoolFilter,
    showCreateModal,
    setShowCreateModal,
    handleCreateSchool,
    handlePatchSchool,
    handleImpersonate,
    handleGetDeletionPreview,
    handleDeleteSchool,
    handleDeleteGpsPositions,
    handleExportGpsPositions,
    logout,
    activeTab,
    setActiveTab,
    globalActivationRate,
    atRiskCount,
    error,
  } = useSuperadmin();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
          <p className="text-sm text-gray-500 mt-0.5">Vue consolidée de toutes les écoles</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
          >
            <Plus size={15} />
            Nouvelle école
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors"
          >
            <LogOut size={15} />
            Déconnexion
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}

      {/* Tabs */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          overview={overview}
          overviewLoading={overviewLoading}
          alerts={alerts}
          alertsLoading={alertsLoading}
          gpsHealth={gpsHealth}
          gpsHealthLoading={gpsHealthLoading}
          gpsPositions={gpsPositions}
          gpsPositionsLoading={gpsPositionsLoading}
          globalActivationRate={globalActivationRate}
          engagementLoading={engagementLoading}
          atRiskCount={atRiskCount}
          schoolsLoading={schoolsLoading}
        />
      )}

      {activeTab === 'schools' && (
        <SchoolsTab
          schools={schools}
          schoolsLoading={schoolsLoading}
          schoolFilter={schoolFilter}
          setSchoolFilter={setSchoolFilter}
          onSelectSchool={(s) => setSelectedSchool(selectedSchool?.id === s.id ? null : s)}
        />
      )}

      {activeTab === 'engagement' && (
        <EngagementTab
          trends={trends}
          trendsLoading={trendsLoading}
          trendDays={trendDays}
          setTrendDays={setTrendDays}
          overview={overview}
          engagement={engagement}
          engagementLoading={engagementLoading}
        />
      )}

      {activeTab === 'infra' && <InfraTab onDeleteGps={handleDeleteGpsPositions} />}

      {/* Modal création école */}
      {showCreateModal && <CreateSchoolModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateSchool} />}

      {/* School drill-down slide-over (disponible depuis tous les onglets) */}
      {selectedSchool && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setSelectedSchool(null)} />
          <SchoolPanel
            school={selectedSchool}
            alerts={alerts}
            engagement={engagement}
            onClose={() => setSelectedSchool(null)}
            onImpersonate={handleImpersonate}
            onPatch={handlePatchSchool}
            onDeleteGps={handleDeleteGpsPositions}
            onExportGps={handleExportGpsPositions}
            onGetDeletionPreview={handleGetDeletionPreview}
            onDeleteSchool={handleDeleteSchool}
            onDeleted={() => setSelectedSchool(null)}
          />
        </>
      )}
    </div>
  );
}
